// Read-side hook for the public Library. Phase 1: just discovery.
// Phase 2 will add publish/fork mutations + a separate squad-scoped query.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type LibraryKind =
  | "protocol_run"
  | "prompt"
  | "guide"
  | "research"
  | "jam"
  | "agent_artifact";

export interface LibraryEntry {
  id: string;
  kind: LibraryKind;
  source_id: string;
  squad_id: string | null;
  publisher_user_id: string;
  title: string;
  summary: string;
  tags: string[];
  payload: Record<string, unknown>;
  visibility: "squad" | "public";
  is_featured: boolean;
  fork_count: number;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export type LibrarySort = "featured" | "trending" | "newest";

export interface LibraryFilters {
  kind?: LibraryKind | "all";
  search?: string;
  sort?: LibrarySort;
}

const PAGE_SIZE = 60;

export const useLibraryEntries = (filters: LibraryFilters = {}) => {
  const { kind = "all", search = "", sort = "featured" } = filters;
  return useQuery({
    queryKey: ["library", "public", kind, search, sort],
    queryFn: async (): Promise<LibraryEntry[]> => {
      let q = supabase
        .from("library_entries")
        .select("*")
        .eq("visibility", "public")
        .limit(PAGE_SIZE);

      if (kind !== "all") q = q.eq("kind", kind);
      if (search.trim()) {
        // Simple ILIKE on title + summary; good enough until we add FTS.
        const term = `%${search.trim()}%`;
        q = q.or(`title.ilike.${term},summary.ilike.${term}`);
      }

      // Featured shelf is sorted client-side after we split; for "trending"
      // we order by fork_count then recency, "newest" by created_at.
      if (sort === "trending") {
        q = q.order("fork_count", { ascending: false }).order("created_at", { ascending: false });
      } else {
        q = q.order("created_at", { ascending: false });
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as LibraryEntry[];
    },
    staleTime: 30_000,
  });
};

// Bumps the view counter for a public entry. Fire-and-forget — failures
// are silent because losing a view count isn't worth surfacing.
export const bumpLibraryView = async (entryId: string) => {
  try {
    await supabase.rpc("bump_library_view", { _entry_id: entryId });
  } catch {
    /* swallow */
  }
};
